import { browser_session_storage } from './lib/browser-storage'
import { PWA_BUNDLE_ERROR_CODE, PWA_ROUTE_ERROR_CODE, render_runtime_error } from './components/runtime-error'

export interface PageModule {
  mount(el: HTMLElement, params?: Record<string, string>): void | Promise<void>
  unmount?(): void
}
export interface Route {
  path: string
  loader: () => Promise<PageModule>
}
type ParamMap = Record<string, string>
interface MatchResult { route: Route; params: ParamMap }

const MODULE_RELOAD_KEY = 'fidelidade_module_reload_once'

function route_loading_html(): string {
  const shell_context = String(document.querySelector('meta[name="app-brand-context"]')?.getAttribute('content') || '').trim()
  const shell_name = String(document.querySelector('meta[name="app-brand-name"]')?.getAttribute('content') || '').trim()
  const eyebrow = (shell_context === 'station' || shell_context === 'rede') && shell_name ? shell_name : 'fidelidade'
  return `
    <section class="boot-shell" data-route-loading aria-live="polite">
      <div class="boot-card" role="status">
        <div class="boot-spinner" aria-hidden="true"></div>
        <p class="boot-eyebrow">${escape_loading_text(eyebrow)}</p>
        <h2 class="boot-title">Abrindo sua tela</h2>
        <p class="boot-copy">Carregando a primeira etapa para evitar tela vazia.</p>
      </div>
    </section>`
}

function escape_loading_text(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function current_location_path(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function should_reload_module_error(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return /Failed to fetch dynamically imported module|Failed to load module script|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed|Module script load error|TypeError: Load failed/i.test(message)
}

async function recover_module_error(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(reg => reg.unregister()))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(key => caches.delete(key)))
    }
  } catch (err) {
    console.warn('[router] falha ao limpar caches apos erro de modulo:', err)
  }
  window.location.reload()
}
// Hrefs que o SPA router NÃO deve interceptar.
//
// - Esquemas não-http (mailto:, tel:, sms:, whatsapp:...): quem resolve é o SO.
//   Sem isso o "Fale conosco" virava preventDefault() + rota inexistente = 404.
// - Documentos estáticos .html (public/privacidade.html, public/termos-de-uso.html):
//   não são rotas do SPA. No navegador o 404 passava despercebido; no app nativo
//   (Capacitor, sem barra de endereço) o usuário ficava preso sem botão de voltar.
const NON_ROUTE_SCHEME = /^[a-z][a-z0-9+.-]*:/i

export function is_non_route_href(href: string): boolean {
  if (NON_ROUTE_SCHEME.test(href)) return true
  return /\.html?($|[?#])/i.test(href)
}

class Router {
  private routes: Route[] = []
  private current_module: PageModule | null = null
  private container: HTMLElement | null = null
  register(path: string, loader: Route['loader']): void {
    this.routes.push({ path, loader })
  }
  register_all(routes: Route[]): void {
    this.routes.push(...routes)
  }
  set_container(el: HTMLElement): void { this.container = el }
  private match(path: string): MatchResult | null {
    const withoutQuery = path.split('?')[0] ?? path
    const clean = withoutQuery.split('#')[0] ?? withoutQuery
    for (const route of this.routes) {
      const params = match_path(route.path, clean)
      if (params) return { route, params }
    }
    return null
  }
  async navigate(path: string, push = true): Promise<void> {
    if (push) window.history.pushState({}, '', path)
    const result = this.match(path)
    if (!result) {
      console.warn('No route matched:', path)
      if (this.container) this.container.innerHTML = '<div class="text-center p-5"><h2>404</h2></div>'
      return
    }
    if (this.current_module?.unmount) this.current_module.unmount()
    const should_keep_boot_shell = !this.current_module
    if (should_keep_boot_shell && this.container) this.container.innerHTML = route_loading_html()
    try {
      const mod = await result.route.loader()
      this.current_module = mod
      if (this.container) {
        if (!should_keep_boot_shell) this.container.innerHTML = ''
        await mod.mount(this.container, result.params)
      }
      browser_session_storage.removeItem(MODULE_RELOAD_KEY)
    } catch (err) {
      const module_error = should_reload_module_error(err)
      if (module_error && !browser_session_storage.getItem(MODULE_RELOAD_KEY)) {
        browser_session_storage.setItem(MODULE_RELOAD_KEY, current_location_path())
        await recover_module_error()
        return
      }
      const error_code = module_error ? PWA_BUNDLE_ERROR_CODE : PWA_ROUTE_ERROR_CODE
      browser_session_storage.removeItem(MODULE_RELOAD_KEY)
      console.error(`[${error_code}] Failed to load route:`, path, err)
      if (this.container) {
        render_runtime_error(
          this.container,
          error_code,
          module_error ? 'Não foi possível atualizar o aplicativo' : 'Não foi possível abrir esta tela',
          'Tente novamente. Se continuar, envie este código ao suporte.',
          {
            phase: module_error ? 'route_bundle' : 'route_mount',
            message: err instanceof Error ? err.message : err,
            stack: err instanceof Error ? err.stack : undefined,
          },
        )
      }
    }
  }
  setup(container?: HTMLElement): void {
    if (container) this.container = container
    window.addEventListener('popstate', () => { void this.navigate(current_location_path(), false) })
    document.addEventListener('click', (e) => {
      const anchor = (e.target as HTMLElement).closest('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('http') || href.startsWith('//')) return
      if (is_non_route_href(href)) return
      if (anchor.hasAttribute('target')) return
      if (anchor.hasAttribute('download')) return
      if (e.ctrlKey || e.metaKey || e.shiftKey) return
      e.preventDefault()
      void this.navigate(href)
    })
  }
  start(container?: HTMLElement): void {
    this.setup(container)
    void this.navigate(current_location_path(), false)
  }
  get_current(): PageModule | null { return this.current_module }
}
function match_path(pattern: string, path: string): ParamMap | null {
  if (pattern === '/' && (path === '/' || path === '')) return {}
  const pattern_parts = pattern.split('/').filter(Boolean)
  const path_parts = path.split('/').filter(Boolean)
  if (pattern_parts.length !== path_parts.length) return null
  const params: ParamMap = {}
  for (const [i, seg] of pattern_parts.entries()) {
    const val = path_parts[i]
    if (val === undefined) return null
    if (seg.startsWith(':')) {
      params[seg.slice(1)] = val
    } else if (seg !== val) {
      return null
    }
  }
  return params
}
export const router = new Router()
// Chat do chatbot no domínio dele (https://<domínio>/ai), no dev e na produção.
router.register('/ai', () => import('./pages/ai-preview'))
router.register('/', () => import('./pages/driver-activation'))
router.register('/empresarial', () => import('./pages/phone'))
router.register('/validar-origem', () => import('./pages/origin-validation'))
router.register('/combustiveis', () => import('./pages/fuels'))
router.register('/avaliacoes', () => import('./pages/driver-reviews'))
router.register('/pagamento/:cod_produto', () => import('./pages/payment'))
router.register('/voucher', () => import('./pages/voucher'))
router.register('/motorista-aplicativo', () => import('./pages/driver-activation'))
router.register('/motorista-aplicativo/ofertas', () => import('./pages/driver-offers'))
router.register('/motorista-aplicativo/voucher', () => import('./pages/driver-voucher'))
router.register('/motorista-aplicativo/vouchers', () => import('./pages/driver-vouchers'))
router.register('/motorista-aplicativo/avaliacoes', () => import('./pages/driver-reviews'))
router.register('/ofertas', () => import('./pages/driver-activation'))
router.register('/ofertas/lista', () => import('./pages/driver-offers'))
router.register('/ofertas/voucher', () => import('./pages/driver-voucher'))
router.register('/ofertas/vouchers', () => import('./pages/driver-vouchers'))
router.register('/ofertas/avaliacoes', () => import('./pages/driver-reviews'))
router.register('/ev', () => import('./pages/ev'))
router.register('/ev/estacoes', () => import('./pages/ev-stations'))
router.register('/ev/estacao/:station_id', () => import('./pages/ev-station'))
router.register('/ev/c/:code', () => import('./pages/ev-qr'))
export function navigate(path: string): void { void router.navigate(path) }
window.__navigate = navigate
